import { Clock, Flag, CalendarClock, UserCheck, Eye, User } from 'lucide-react';
import type { RequestItem, RequestStatus } from '../types';
import { formatDate, formatDateTime } from '../lib/format';
import { Modal, StatusBadge, inputClass, Avatar } from './ui';

const REQUEST_STATUSES: RequestStatus[] = ['open', 'in_progress', 'resolved', 'dismissed'];

const priorityColor: Record<string, string> = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-red-600',
};

interface Props {
  request: RequestItem | null;
  canManage: boolean;
  onClose: () => void;
  onStatusChange: (id: number, status: RequestStatus) => void;
}

export default function RequestDetailModal({ request, canManage, onClose, onStatusChange }: Props) {
  if (!request) return null;

  const r = request;

  return (
    <Modal open={!!request} onClose={onClose} title={r.title} maxWidth="max-w-2xl">
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center">
            <Avatar src={r.image_url} name={r.user_name} className="w-10 h-10 mr-3" />
            <div>
              <p className="font-semibold text-gray-800">{r.user_name}</p>
              <p className="text-xs text-gray-500 capitalize">{r.user_role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={r.status} />
            <span className={`inline-flex items-center text-xs font-medium ${priorityColor[r.priority]}`}>
              <Flag className="h-3.5 w-3.5 mr-1" /> {r.priority}
            </span>
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-3">
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              className={`${inputClass} !w-auto !py-1.5`}
              value={r.status}
              onChange={(e) => onStatusChange(r.id, e.target.value as RequestStatus)}
            >
              {REQUEST_STATUSES.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
        )}

        {r.description ? (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
            <p className="text-sm text-gray-700 whitespace-pre-line">{r.description}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No description provided.</p>
        )}

        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
          <span className="inline-flex items-center">
            <Clock className="h-3.5 w-3.5 mr-1" />
            Created {formatDateTime(r.created_at)} {r.updated_at !== r.created_at && `· Updated ${formatDateTime(r.updated_at)}`}
          </span>
          {r.due_date && (
            <span className="inline-flex items-center text-amber-600">
              <CalendarClock className="h-3.5 w-3.5 mr-1" /> Due {formatDate(r.due_date)}
            </span>
          )}
        </div>

        {r.responded_by_name && (
          <div className="flex items-center text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
            <UserCheck className="h-4 w-4 mr-2" />
            Responded by {r.responded_by_name}
            {r.responded_at ? ` on ${formatDateTime(r.responded_at)}` : ''}
          </div>
        )}

        {r.read_by_name && (
          <div className="flex items-center text-sm text-primary-dark bg-primary/10 rounded-lg px-3 py-2">
            <Eye className="h-4 w-4 mr-2" />
            Read by {r.read_by_name}
            {r.read_at ? ` on ${formatDateTime(r.read_at)}` : ''}
          </div>
        )}

        {!r.read_by_name && (
          <div className="flex items-center text-sm text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            <Eye className="h-4 w-4 mr-2" />
            Not yet read by an admin or manager
          </div>
        )}

        <p className="text-xs text-gray-400 flex items-center">
          <User className="h-3.5 w-3.5 mr-1" /> Request ID #{r.id}
        </p>
      </div>
    </Modal>
  );
}
